import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import styles from '../assets/inspections.module.css';
import inspectionsService from '../services/inspectionsService';
import { propertiesMonthlyInspectionsService } from '../services/propertiesMonthlyInspectionsService';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);


const Inspections = () => {
  const navigate = useNavigate();
  const { hasPermission, isGuardianManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inspections, setInspections] = useState([]);
  const [monthlyInspections, setMonthlyInspections] = useState([]);
  const [monthlyInspectionsLoading, setMonthlyInspectionsLoading] = useState(false);
  const [buildingManagers, setBuildingManagers] = useState([]);
  const [types, setTypes] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [accountManagers, setAccountManagers] = useState([]);
  
  // DataTables control state
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [monthlyEntriesPerPage, setMonthlyEntriesPerPage] = useState(30);
  const [tableSearch, setTableSearch] = useState('');
  const [monthlyTableSearch, setMonthlyTableSearch] = useState('');
  const [filteredInspections, setFilteredInspections] = useState([]);
  const [filteredMonthlyInspections, setFilteredMonthlyInspections] = useState([]);
  
  // Pagination state for inspections table
  const [currentPageInspections, setCurrentPageInspections] = useState(1);
  
  // Pagination state for monthly inspections table
  const [currentPageMonthly, setCurrentPageMonthly] = useState(1);
  
  // Status overview data
  const [inspectionsAll, setInspectionsAll] = useState(0);
  const [inspectionsUpcoming, setInspectionsUpcoming] = useState(0);
  const [inspectionsPast, setInspectionsPast] = useState(0);
  
  // Properties overview data
  const [propertiesUpcoming, setPropertiesUpcoming] = useState(0);
  const [propertiesInspectionScheduled, setPropertiesInspectionScheduled] = useState(0);
  const [propertiesInspectionCompleted, setPropertiesInspectionCompleted] = useState(0);
  const [propertiesToBePublished, setPropertiesToBePublished] = useState(0);
  const [propertiesCompleted, setPropertiesCompleted] = useState(0);
  const [propertiesOverdue, setPropertiesOverdue] = useState(0);
  
  // Status filter state
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(null); // null = all, 'upcoming' = upcoming, 'past' = past
  
  // Property status filter state for monthly inspections
  const [selectedPropertyStatusFilter, setSelectedPropertyStatusFilter] = useState(null); // null = all, 'upcoming', 'scheduled', 'completed', 'to_be_published', 'past_completed', 'overdue'

  // Search form state
  const [searchParams, setSearchParams] = useState({
    building_manager_id: 'all',
    date_from: '',
    date_to: '',
    type_id: 'all',
    searchTerm: ''
  });

  // Monthly inspections search form state
  const [monthlySearchParams, setMonthlySearchParams] = useState({
    account_manager_id: 'all',
    route_id: 'all',
    client_id: '',
    client_name: '',
    property_id: '',
    property_name: ''
  });

  // Typeahead state for monthly inspections
  const [clientTypeaheadResults, setClientTypeaheadResults] = useState([]);
  const [showClientTypeahead, setShowClientTypeahead] = useState(false);
  const [propertyTypeaheadResults, setPropertyTypeaheadResults] = useState([]);
  const [showPropertyTypeahead, setShowPropertyTypeahead] = useState(false);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    inspections: {
      property: true,
      id: true,
      postcode: true,
      buildingManager: true,
      day: true,
      from: true,
      to: true,
      type: true,
      route: true,
      description: true,
      calendar: true,
      actions: true
    },
    monthly: {
      property: true,
      id: true,
      postcode: true,
      company: true,
      dateLive: true,
      am: true,
      bm: true,
      route: true,
      status: true,
      actions: true
    }
  });

  const [showColumnVisibility, setShowColumnVisibility] = useState({
    inspections: false,
    monthly: false
  });

  // State for table sorting
  const [sortField, setSortField] = useState("day");
  const [sortDirection, setSortDirection] = useState("asc");
  const [monthlySortField, setMonthlySortField] = useState("property");
  const [monthlySortDirection, setMonthlySortDirection] = useState("asc");

  const [isStatusDescriptionsCollapsed, setIsStatusDescriptionsCollapsed] = useState(false);

  // Load inspections data - LIVE DATA ONLY
  const loadInspections = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📡 [FRONTEND] Loading inspections data from live database...');
      
      // Use real backend API only - no mock data
      const response = await inspectionsService.getInspectionsIndex();
      
      if (response.success) {
        const { data } = response;
        
        // Set all data from backend response
        setInspections(data.inspections || []);
        setBuildingManagers(data.buildingManagers || []);
        setTypes(data.types || []);
        setRoutes(data.routes || []);
        setAccountManagers(data.accountManagers || []);
        
        // Set status overview data
        const statusOverview = data.statusOverview || {};
        setInspectionsAll(statusOverview.inspectionsAll || 0);
        setInspectionsUpcoming(statusOverview.inspectionsUpcoming || 0);
        setInspectionsPast(statusOverview.inspectionsPast || 0);
        
        // Set properties overview data
        const propertiesOverview = data.propertiesOverview || {};
        setPropertiesUpcoming(propertiesOverview.propertiesUpcoming || 0);
        setPropertiesInspectionScheduled(propertiesOverview.propertiesInspectionScheduled || 0);
        setPropertiesInspectionCompleted(propertiesOverview.propertiesInspectionCompleted || 0);
        setPropertiesToBePublished(propertiesOverview.propertiesToBePublished || 0);
        setPropertiesCompleted(propertiesOverview.propertiesCompleted || 0);
        setPropertiesOverdue(propertiesOverview.propertiesOverdue || 0);
        
        console.log('✅ [FRONTEND] Live database data loaded successfully:', {
          inspectionsCount: data.inspections?.length || 0,
          source: data.source,
          statusOverview,
          propertiesOverview
        });
      } else {
        throw new Error(response.message || 'Failed to load inspections');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading inspections:', error);
      toast.error('Failed to load inspections from database. Please check your connection.');
      // No fallback - show empty state
      setInspections([]);
      setMonthlyInspections([]);
      setBuildingManagers([]);
      setTypes([]);
      setRoutes([]);
      setAccountManagers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load monthly inspections properties - LIVE DATA ONLY
  // Now accepts optional status filter parameter
  const loadMonthlyInspections = useCallback(async (statusFilter = null) => {
    try {
      setMonthlyInspectionsLoading(true);
      console.log('📡 [FRONTEND] Loading monthly inspections properties from live database...', { statusFilter });
      
      // Use real backend API only - no mock data
      // Send DataTables-compatible request with draw parameter
      // If status filter is provided, include it in the request
      const requestParams = {
        draw: 1,
        start: 0,
        length: 10000 // Get all properties (no pagination for now)
      };
      
      // Map frontend filter IDs to backend status values if needed
      if (statusFilter) {
        const statusMap = {
          'upcoming': 'To be Scheduled',
          'scheduled': 'Inspection Scheduled',
          'completed': 'Completed',
          'to_be_published': 'To be published',
          'past_completed': 'Past Inspection To Complete',
          'overdue': 'Overdue'
        };
        requestParams.status = statusMap[statusFilter] || statusFilter;
      }
      
      // Include account manager filter if selected
      if (monthlySearchParams.account_manager_id && monthlySearchParams.account_manager_id !== 'all') {
        requestParams.account_manager_id = monthlySearchParams.account_manager_id;
      }
      
      // Include route filter if selected
      if (monthlySearchParams.route_id && monthlySearchParams.route_id !== 'all') {
        requestParams.route_id = monthlySearchParams.route_id;
      }
      
      const response = await propertiesMonthlyInspectionsService.getProperties(requestParams);
      
      console.log('📡 [FRONTEND] API Response:', {
        hasData: !!response.data,
        dataStructure: response.data ? Object.keys(response.data) : [],
        hasDraw: !!response.data?.draw,
        hasRecordsTotal: !!response.data?.recordsTotal,
        hasDataArray: Array.isArray(response.data?.data),
        propertiesCount: response.data?.data?.length || 0
      });
      
      // Laravel DataTables format: { draw, recordsTotal, recordsFiltered, data }
      if (response && response.data && Array.isArray(response.data.data)) {
        const properties = response.data.data;
        
        console.log('📡 [FRONTEND] Extracted properties:', {
          count: properties.length,
          firstProperty: properties[0] || null
        });
        
        if (properties.length === 0) {
          console.log('⚠️ [FRONTEND] No properties found in response');
          setMonthlyInspections([]);
          return;
        }
        
        // Transform properties data to match frontend expectations
        const transformedProperties = properties.map(property => ({
          id: property.id,
          property: {
            id: property.id,
            name: property.propertyName || property.name,
            property_id: property.propertyPublicId || property.property_id,
            postcode: property.propertyPostcode || property.postcode,
          },
          client: {
            id: property.clientId || null,
            name: property.clientName || 'Unknown Client',
          },
          date_live: property.dateLive || property.date_live,
          accountManager: property.accountManager || 'Not Assigned',
          accountManagerId: property.accountManagerId || null,
          buildingManager: {
            id: property.buildingManagerId || null,
            name: property.buildingManager || 'Not Assigned',
          },
          route: {
            id: property.routeId || null,
            name: property.routeName || 'No Route',
          },
          monthly_inspection_status: property.monthlyInspectionStatus || property.monthly_inspection_status || 'To be Scheduled',
          statusColor: property.statusColor || '#5bc0de',
        }));
        
        console.log('📡 [FRONTEND] Setting monthlyInspections state with:', {
          count: transformedProperties.length,
          firstProperty: transformedProperties[0] || null,
          allProperties: transformedProperties
        });
        
        setMonthlyInspections(transformedProperties);
        
        console.log('✅ [FRONTEND] Monthly inspections properties loaded successfully:', {
          propertiesCount: transformedProperties.length,
          recordsTotal: response.data.recordsTotal,
          recordsFiltered: response.data.recordsFiltered
        });
      } else {
        console.error('❌ [FRONTEND] Invalid response structure:', {
          response: response,
          responseData: response?.data,
          hasData: !!response?.data,
          isArray: Array.isArray(response?.data?.data),
          structure: response?.data ? Object.keys(response.data) : []
        });
        // If response exists but no data array, set empty array
        if (response && response.data && !Array.isArray(response.data.data)) {
          console.log('⚠️ [FRONTEND] Response exists but data is not an array, setting empty array');
          setMonthlyInspections([]);
          return;
        }
        throw new Error('Failed to load monthly inspections properties - invalid response structure');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading monthly inspections properties:', error);
      console.error('❌ [FRONTEND] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method
      });
      
      // Check if error response has DataTables format
      if (error.response?.data && Array.isArray(error.response.data.data)) {
        console.log('⚠️ [FRONTEND] Error response has DataTables format, using empty data array');
        setMonthlyInspections([]);
        return;
      }
      
      toast.error('Failed to load monthly inspections properties from database. Please check your connection.');
      // No fallback - show empty state
      setMonthlyInspections([]);
    } finally {
      setMonthlyInspectionsLoading(false);
    }
  }, [monthlySearchParams.account_manager_id, monthlySearchParams.route_id]);

  // Load data on component mount - only load inspections, NOT monthly inspections
  useEffect(() => {
    console.log('🔄 [FRONTEND] Component mounted, loading data...');
    loadInspections();
    // Don't load monthly inspections on mount - only load when filter is clicked
    // loadMonthlyInspections();
  }, [loadInspections]);
  
  // Debug: Log monthlyInspections state changes
  useEffect(() => {
    console.log('📊 [FRONTEND] monthlyInspections state changed:', {
      count: monthlyInspections.length,
      firstProperty: monthlyInspections[0] || null
    });
  }, [monthlyInspections]);

  // Close column visibility dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.columnVisibilityDropdown}`) && 
          !event.target.closest(`.${styles.visibilityBtn}`)) {
        setShowColumnVisibility({
          inspections: false,
          monthly: false
        });
      }
      
      // Close typeahead dropdowns when clicking outside
      if (!event.target.closest('.js-client-field-typeahead') && 
          !event.target.closest('.js-property-field-typeahead')) {
        setShowClientTypeahead(false);
        setShowPropertyTypeahead(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter inspections based on search and entries per page
  useEffect(() => {
    let filtered = inspections;
    
    // Apply status filter first (from status overview boxes)
    if (selectedStatusFilter === 'upcoming') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(inspection => 
        inspection.day >= today
      );
    } else if (selectedStatusFilter === 'past') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(inspection => 
        inspection.day < today
      );
    }
    // If selectedStatusFilter is null, show all (no filter)
    
    // Apply form filters
    if (searchParams.building_manager_id && searchParams.building_manager_id !== 'all') {
      filtered = filtered.filter(inspection => 
        inspection.building_manager.id.toString() === searchParams.building_manager_id
      );
    }
    
    if (searchParams.date_from) {
      filtered = filtered.filter(inspection => 
        inspection.day >= searchParams.date_from
      );
    }
    
    if (searchParams.date_to) {
      filtered = filtered.filter(inspection => 
        inspection.day <= searchParams.date_to
      );
    }
    
    if (searchParams.type_id && searchParams.type_id !== 'all') {
      filtered = filtered.filter(inspection => 
        inspection.type.id.toString() === searchParams.type_id
      );
    }
    
    if (searchParams.searchTerm) {
      const term = searchParams.searchTerm.toLowerCase();
      filtered = filtered.filter(inspection => 
        inspection.property.name.toLowerCase().includes(term) ||
        inspection.property.postcode.toLowerCase().includes(term) ||
        inspection.building_manager.name.toLowerCase().includes(term) ||
        inspection.description.toLowerCase().includes(term)
      );
    }
    
    // Apply table search filter
    if (tableSearch) {
      filtered = filtered.filter(inspection => 
        inspection.property.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.property.postcode.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.building_manager.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.type.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.route.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.description.toLowerCase().includes(tableSearch.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "property") {
        aValue = a.property?.name || "";
        bValue = b.property?.name || "";
      } else if (sortField === "id") {
        aValue = a.id || 0;
        bValue = b.id || 0;
      } else if (sortField === "postcode") {
        aValue = a.property?.postcode || "";
        bValue = b.property?.postcode || "";
      } else if (sortField === "buildingManager") {
        aValue = a.building_manager?.name || "";
        bValue = b.building_manager?.name || "";
      } else if (sortField === "day") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      } else if (sortField === "from") {
        aValue = a.time_from || "";
        bValue = b.time_from || "";
      } else if (sortField === "to") {
        aValue = a.time_to || "";
        bValue = b.time_to || "";
      } else if (sortField === "type") {
        aValue = a.type?.name || "";
        bValue = b.type?.name || "";
      } else if (sortField === "route") {
        aValue = a.route?.name || "";
        bValue = b.route?.name || "";
      } else if (sortField === "description") {
        aValue = a.description || "";
        bValue = b.description || "";
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    // Store all filtered results (for pagination)
    const allFiltered = filtered;
    
    // Calculate pagination
    const totalPages = Math.ceil(allFiltered.length / entriesPerPage) || 1;
    const startIndex = (currentPageInspections - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    
    // Apply pagination - slice for current page
    filtered = allFiltered.slice(startIndex, endIndex);
    
    setFilteredInspections(filtered);
  }, [inspections, tableSearch, entriesPerPage, searchParams, sortField, sortDirection, selectedStatusFilter, currentPageInspections]);

  // Filter monthly inspections based on search
  useEffect(() => {
    let filtered = monthlyInspections;
    
    // Apply form filters first
    // Note: Account manager and route filters are now applied on backend when loading data
    // But we still filter client-side for any properties that were loaded before filter changed
    if (monthlySearchParams.account_manager_id && monthlySearchParams.account_manager_id !== 'all') {
      filtered = filtered.filter(property => {
        // Filter by account manager ID if available, otherwise match by name
        if (property.accountManagerId) {
          return property.accountManagerId.toString() === monthlySearchParams.account_manager_id.toString();
        }
        // Fallback: match by account manager name
        const selectedManager = accountManagers.find(m => m.id.toString() === monthlySearchParams.account_manager_id.toString());
        if (selectedManager) {
          return property.accountManager === selectedManager.name;
        }
        return false;
      });
    }
    
    if (monthlySearchParams.route_id && monthlySearchParams.route_id !== 'all') {
      filtered = filtered.filter(property => 
        property.route.id && property.route.id.toString() === monthlySearchParams.route_id
      );
    }
    
    // Filter by client ID if selected, otherwise filter by name
    if (monthlySearchParams.client_id) {
      filtered = filtered.filter(property => 
        property.client?.id?.toString() === monthlySearchParams.client_id.toString()
      );
    } else if (monthlySearchParams.client_name) {
      const clientName = monthlySearchParams.client_name.toLowerCase();
      filtered = filtered.filter(property => 
        property.client?.name?.toLowerCase().includes(clientName) ||
        property.property?.name?.toLowerCase().includes(clientName)
      );
    }
    
    // Filter by property ID if selected, otherwise filter by name
    if (monthlySearchParams.property_id) {
      filtered = filtered.filter(property => 
        property.property?.id?.toString() === monthlySearchParams.property_id.toString()
      );
    } else if (monthlySearchParams.property_name) {
      const propertyName = monthlySearchParams.property_name.toLowerCase();
      filtered = filtered.filter(property => 
        property.property?.name?.toLowerCase().includes(propertyName)
      );
    }
    
    // Apply property status filter from overview boxes
    if (selectedPropertyStatusFilter) {
      const statusMap = {
        'upcoming': ['To be Scheduled', 'Upcoming', 'To Be Scheduled'],
        'scheduled': ['Inspection Scheduled', 'Scheduled'],
        'completed': ['Completed'],
        'to_be_published': ['To be published', 'To Be Published'],
        'past_completed': ['Past Inspection To Complete', 'Past Inspection To Complete'],
        'overdue': ['Overdue']
      };
      
      const allowedStatuses = statusMap[selectedPropertyStatusFilter] || [];
      if (allowedStatuses.length > 0) {
        const beforeFilterCount = filtered.length;
        filtered = filtered.filter(property => {
          const propertyStatus = property.monthly_inspection_status || '';
          const matches = allowedStatuses.some(status => 
            propertyStatus.toLowerCase() === status.toLowerCase()
          );
          return matches;
        });
        
        console.log('📊 [FRONTEND] Property status filter applied:', {
          filter: selectedPropertyStatusFilter,
          allowedStatuses: allowedStatuses,
          beforeCount: beforeFilterCount,
          afterCount: filtered.length,
          sampleStatuses: [...new Set(filtered.slice(0, 5).map(p => p.monthly_inspection_status))]
        });
      }
    }
    
    // Apply search filter
    if (monthlyTableSearch) {
      filtered = filtered.filter(property => 
        property.property.name.toLowerCase().includes(monthlyTableSearch.toLowerCase()) ||
        property.property.postcode.toLowerCase().includes(monthlyTableSearch.toLowerCase()) ||
        property.buildingManager.name.toLowerCase().includes(monthlyTableSearch.toLowerCase()) ||
        property.route.name.toLowerCase().includes(monthlyTableSearch.toLowerCase()) ||
        property.client.name.toLowerCase().includes(monthlyTableSearch.toLowerCase())
      );
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue = a[monthlySortField];
      let bValue = b[monthlySortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (monthlySortField === "property") {
        aValue = a.property?.name || "";
        bValue = b.property?.name || "";
      } else if (monthlySortField === "id") {
        aValue = a.property?.property_id || a.property?.id || 0;
        bValue = b.property?.property_id || b.property?.id || 0;
      } else if (monthlySortField === "postcode") {
        aValue = a.property?.postcode || "";
        bValue = b.property?.postcode || "";
      } else if (monthlySortField === "company") {
        aValue = a.client?.name || "";
        bValue = b.client?.name || "";
      } else if (monthlySortField === "dateLive") {
        aValue = new Date(a.date_live || 0);
        bValue = new Date(b.date_live || 0);
      } else if (monthlySortField === "am") {
        aValue = a.accountManager || "";
        bValue = b.accountManager || "";
      } else if (monthlySortField === "bm") {
        aValue = a.buildingManager?.name || "";
        bValue = b.buildingManager?.name || "";
      } else if (monthlySortField === "route") {
        aValue = a.route?.name || "";
        bValue = b.route?.name || "";
      } else if (monthlySortField === "status") {
        aValue = a.monthly_inspection_status || "";
        bValue = b.monthly_inspection_status || "";
      }

      if (aValue < bValue) return monthlySortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return monthlySortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    // Store all filtered results (for pagination)
    const allFiltered = filtered;
    
    // Calculate pagination for monthly inspections
    const totalPages = Math.ceil(allFiltered.length / monthlyEntriesPerPage) || 1;
    const startIndex = (currentPageMonthly - 1) * monthlyEntriesPerPage;
    const endIndex = startIndex + monthlyEntriesPerPage;
    
    // Apply pagination - slice for current page
    filtered = allFiltered.slice(startIndex, endIndex);
    
    console.log('📊 [FRONTEND] Monthly inspections filtering:', {
      originalCount: monthlyInspections.length,
      allFilteredCount: allFiltered.length,
      paginatedCount: filtered.length,
      currentPage: currentPageMonthly,
      totalPages: totalPages,
      startIndex: startIndex,
      endIndex: endIndex,
      filters: monthlySearchParams,
      searchTerm: monthlyTableSearch,
      sortField: monthlySortField,
      sortDirection: monthlySortDirection
    });
    
    setFilteredMonthlyInspections(filtered);
  }, [monthlyInspections, monthlyTableSearch, monthlySearchParams, monthlySortField, monthlySortDirection, currentPageMonthly, monthlyEntriesPerPage, selectedPropertyStatusFilter]);

  // Reset pagination when search terms change
  // Use individual properties to avoid infinite loops with object dependencies
  useEffect(() => {
    setCurrentPageInspections(1);
  }, [
    tableSearch, 
    searchParams.building_manager_id,
    searchParams.date_from,
    searchParams.date_to,
    searchParams.type_id,
    searchParams.searchTerm
  ]);

  useEffect(() => {
    setCurrentPageMonthly(1);
  }, [
    monthlyTableSearch,
    monthlySearchParams.account_manager_id,
    monthlySearchParams.route_id,
    monthlySearchParams.client_id,
    monthlySearchParams.client_name,
    monthlySearchParams.property_id,
    monthlySearchParams.property_name,
    selectedPropertyStatusFilter
  ]);

  // Calculate total filtered count for inspections (before pagination)
  const getTotalFilteredInspections = () => {
    let filtered = inspections;
    
    // Apply status filter
    if (selectedStatusFilter === 'upcoming') {
      filtered = filtered.filter(inspection => {
        const inspectionDate = new Date(inspection.day);
        return inspectionDate >= new Date();
      });
    } else if (selectedStatusFilter === 'past') {
      filtered = filtered.filter(inspection => {
        const inspectionDate = new Date(inspection.day);
        return inspectionDate < new Date();
      });
    }
    
    // Apply search form filters
    if (searchParams.building_manager_id && searchParams.building_manager_id !== 'all') {
      filtered = filtered.filter(inspection => 
        inspection.building_manager.id.toString() === searchParams.building_manager_id
      );
    }
    
    if (searchParams.type_id && searchParams.type_id !== 'all') {
      filtered = filtered.filter(inspection => 
        inspection.type.id.toString() === searchParams.type_id
      );
    }
    
    if (searchParams.date_from) {
      filtered = filtered.filter(inspection => {
        const inspectionDate = new Date(inspection.day);
        const fromDate = new Date(searchParams.date_from);
        return inspectionDate >= fromDate;
      });
    }
    
    if (searchParams.date_to) {
      filtered = filtered.filter(inspection => {
        const inspectionDate = new Date(inspection.day);
        const toDate = new Date(searchParams.date_to);
        return inspectionDate <= toDate;
      });
    }
    
    if (searchParams.searchTerm) {
      const searchTerm = searchParams.searchTerm.toLowerCase();
      filtered = filtered.filter(inspection => 
        inspection.property.name.toLowerCase().includes(searchTerm) ||
        inspection.property.postcode.toLowerCase().includes(searchTerm) ||
        inspection.description.toLowerCase().includes(searchTerm)
      );
    }
    
    // Apply table search filter
    if (tableSearch) {
      filtered = filtered.filter(inspection => 
        inspection.property.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.property.postcode.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.building_manager.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.type.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.route.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        inspection.description.toLowerCase().includes(tableSearch.toLowerCase())
      );
    }
    
    return filtered.length;
  };
  
  // Calculate total filtered count for monthly inspections (before pagination)
  const getTotalFilteredMonthlyInspections = () => {
    let filtered = monthlyInspections;
    
    // Apply form filters
    if (monthlySearchParams.account_manager_id && monthlySearchParams.account_manager_id !== 'all') {
      // Backend should handle this filter
    }
    
    if (monthlySearchParams.route_id && monthlySearchParams.route_id !== 'all') {
      filtered = filtered.filter(property => 
        property.route.id && property.route.id.toString() === monthlySearchParams.route_id
      );
    }
    
    if (monthlySearchParams.client_id) {
      filtered = filtered.filter(property => 
        property.client?.id?.toString() === monthlySearchParams.client_id.toString()
      );
    } else if (monthlySearchParams.client_name) {
      const clientName = monthlySearchParams.client_name.toLowerCase();
      filtered = filtered.filter(property => 
        property.client?.name?.toLowerCase().includes(clientName) ||
        property.property?.name?.toLowerCase().includes(clientName)
      );
    }
    
    if (monthlySearchParams.property_id) {
      filtered = filtered.filter(property => 
        property.property?.id?.toString() === monthlySearchParams.property_id.toString()
      );
    } else if (monthlySearchParams.property_name) {
      const propertyName = monthlySearchParams.property_name.toLowerCase();
      filtered = filtered.filter(property => 
        property.property?.name?.toLowerCase().includes(propertyName) ||
        property.property?.property_id?.toLowerCase().includes(propertyName)
      );
    }
    
    // Apply table search filter
    if (monthlyTableSearch) {
      const searchTerm = monthlyTableSearch.toLowerCase();
      filtered = filtered.filter(property => 
        property.property?.name?.toLowerCase().includes(searchTerm) ||
        property.property?.property_id?.toLowerCase().includes(searchTerm) ||
        property.property?.postcode?.toLowerCase().includes(searchTerm) ||
        property.client?.name?.toLowerCase().includes(searchTerm) ||
        property.accountManager?.toLowerCase().includes(searchTerm) ||
        property.buildingManager?.name?.toLowerCase().includes(searchTerm) ||
        property.route?.name?.toLowerCase().includes(searchTerm) ||
        property.monthly_inspection_status?.toLowerCase().includes(searchTerm)
      );
    }
    
    return filtered.length;
  };
  
  // Pagination handlers for inspections table
  const handleInspectionsPageChange = (newPage) => {
    setCurrentPageInspections(newPage);
  };
  
  const handleInspectionsEntriesPerPageChange = (newEntriesPerPage) => {
    setEntriesPerPage(newEntriesPerPage);
    setCurrentPageInspections(1); // Reset to first page
  };
  
  // Pagination handlers for monthly inspections table
  const handleMonthlyPageChange = (newPage) => {
    setCurrentPageMonthly(newPage);
  };
  
  const handleMonthlyEntriesPerPageChange = (newEntriesPerPage) => {
    setMonthlyEntriesPerPage(newEntriesPerPage);
    setCurrentPageMonthly(1); // Reset to first page
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPageInspections(1); // Reset to first page on search
    toast.success(`Found ${getTotalFilteredInspections()} inspections matching your criteria`);
  };

  const handleSearchChange = (field, value) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMonthlySearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPageMonthly(1); // Reset to first page on search
    toast.success(`Found ${getTotalFilteredMonthlyInspections()} monthly inspections matching your criteria`);
  };

  const handleMonthlySearchChange = (field, value) => {
    setMonthlySearchParams(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Reload data when account manager or route filter changes (backend filters)
    if (field === 'account_manager_id' || field === 'route_id') {
      // Reset to first page
      setCurrentPageMonthly(1);
      // Reload data with current filters
      // Use selectedPropertyStatusFilter if set, otherwise null (load all properties with filter)
      const statusFilter = selectedPropertyStatusFilter;
      // Always reload when account manager or route changes, even if no status filter
      loadMonthlyInspections(statusFilter);
    }
  };

  // Handle client search for typeahead
  const handleClientSearch = async (value) => {
    setMonthlySearchParams(prev => ({
      ...prev,
      client_name: value,
      client_id: '' // Clear ID when name changes
    }));

    if (value.length >= 1) {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${backendUrl}/api/clients/ajax/${encodeURIComponent(value)}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.data) {
          setClientTypeaheadResults(data.data);
          setShowClientTypeahead(true);
        } else {
          setClientTypeaheadResults([]);
          setShowClientTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching clients:', error);
        setClientTypeaheadResults([]);
        setShowClientTypeahead(false);
      }
    } else {
      setClientTypeaheadResults([]);
      setShowClientTypeahead(false);
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    setMonthlySearchParams(prev => ({
      ...prev,
      client_id: client.id,
      client_name: client.name
    }));
    setShowClientTypeahead(false);
  };

  // Handle property search for typeahead
  const handlePropertySearch = async (value) => {
    setMonthlySearchParams(prev => ({
      ...prev,
      property_name: value,
      property_id: '' // Clear ID when name changes
    }));

    if (value.length >= 1) {
      try {
        const response = await inspectionsService.searchProperties(value);
        if (response && response.success && response.data) {
          setPropertyTypeaheadResults(response.data);
          setShowPropertyTypeahead(true);
        } else {
          setPropertyTypeaheadResults([]);
          setShowPropertyTypeahead(false);
        }
      } catch (error) {
        console.error('Error searching properties:', error);
        setPropertyTypeaheadResults([]);
        setShowPropertyTypeahead(false);
      }
    } else {
      setPropertyTypeaheadResults([]);
      setShowPropertyTypeahead(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (property) => {
    setMonthlySearchParams(prev => ({
      ...prev,
      property_id: property.id,
      property_name: property.name
    }));
    setShowPropertyTypeahead(false);
  };

  const handleAddInspection = () => {
    navigate('/inspections/create');
  };

  const handleEditInspection = (id) => {
    navigate(`/inspections/${id}/edit`);
  };

  const handleDeleteInspection = async (id) => {
    // Find the inspection to get its details for confirmation
    const inspection = inspections.find(insp => insp.id === id);
    const inspectionName = inspection ? `"${inspection.description}" for ${inspection.property.name}` : `ID ${id}`;
    
    const confirmMessage = `Are you sure you want to PERMANENTLY DELETE inspection ${inspectionName} and ALL related records?\n\nThis action will delete:\n• The inspection record\n• All monthly inspection reports\n• All initial inspection reports\n• All inspection notes\n• All inspection photos\n• All inspection documents\n• All audit logs\n• All related data\n\nThis action CANNOT be undone!`;
    
    if (window.confirm(confirmMessage)) {
      try {
        console.log(`🗑️ [FRONTEND] Starting deletion for inspection ID: ${id}`);
        
        // Call the API to delete the inspection and related records
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inspections/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ [FRONTEND] Deletion result:', result);
          
          // Remove from local state
          setInspections(prev => prev.filter(inspection => inspection.id !== id));
          setFilteredInspections(prev => prev.filter(inspection => inspection.id !== id));
          
          // Show enhanced success message with detailed information
          if (result.data && result.data.totalRecordsDeleted > 1) {
            const deletedTypes = result.data.deletedRecordTypes || [];
            const typesText = deletedTypes.length > 0 ? ` (${deletedTypes.join(', ')})` : '';
            toast.success(
              `🗑️ HARD DELETED: Inspection "${result.data.inspection.description}" and ${result.data.totalRecordsDeleted - 1} related records${typesText} permanently deleted from database!`,
              { autoClose: 8000 }
            );
          } else {
            toast.success(`🗑️ Inspection permanently deleted from database!`);
          }
          
          // Refresh the inspections data to get updated counts
          loadInspections();
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete inspection');
        }
      } catch (error) {
        console.error('❌ [FRONTEND] Error deleting inspection:', error);
        toast.error(`Failed to delete inspection: ${error.message}. Please try again.`);
      }
    }
  };

  const handleGoToBuildingManagerCalendar = (buildingManagerId) => {
    if (buildingManagerId && buildingManagerId !== 'all') {
      navigate(`/inspections/building-manager/${buildingManagerId}`);
    }
  };

  const handleSendReminderEmail = (buildingManagerId) => {
    if (buildingManagerId && buildingManagerId !== 'all') {
      navigate(`/inspections/building-manager/${buildingManagerId}/email-reminder/create`);
    }
  };

  // Check if user has permission to manage inspections
  const canManageInspections = hasPermission('inspections.manage');

  const handleStatusBoxClick = (statusId) => {
    console.log('Status box clicked:', statusId);
    
    // Map statusId to filter type
    // 999 = all, 1 = upcoming, 2 = past (based on Laravel config)
    let filterType = null;
    if (statusId === '999' || statusId === 'all') {
      filterType = null; // Show all
    } else if (statusId === '1' || statusId === 'upcoming') {
      filterType = 'upcoming';
    } else if (statusId === '2' || statusId === 'past') {
      filterType = 'past';
    }
    
    setSelectedStatusFilter(filterType);
    
    // Show feedback
    const statusText = filterType === null ? 'All' : filterType === 'upcoming' ? 'Upcoming' : 'Past';
    toast.info(`Filtering by status: ${statusText}`);
  };

  const handleActivityBoxClick = (statusId) => {
    console.log('Activity box clicked:', statusId);
    
    // Toggle filter: if same status is clicked again, clear filter
    if (selectedPropertyStatusFilter === statusId) {
      setSelectedPropertyStatusFilter(null);
      // If account manager or route filter is active, reload with those filters only
      if ((monthlySearchParams.account_manager_id && monthlySearchParams.account_manager_id !== 'all') ||
          (monthlySearchParams.route_id && monthlySearchParams.route_id !== 'all')) {
        loadMonthlyInspections(null); // Reload with account manager/route filters but no status filter
      } else {
        setMonthlyInspections([]); // Clear table data if no other filters
        setFilteredMonthlyInspections([]); // Clear filtered data
      }
      toast.info('Property status filter cleared');
    } else {
      setSelectedPropertyStatusFilter(statusId);
      toast.info(`Loading properties with status: ${statusId}...`);
      
      // Load data with the selected filter (and any account manager/route filters)
      loadMonthlyInspections(statusId);
    }
    
    // Reset to first page when filter changes
    setCurrentPageMonthly(1);
  };

  const handleExportCSV = (tableType = 'inspections') => {
    const data = tableType === 'inspections' ? filteredInspections : filteredMonthlyInspections;
    const headers = tableType === 'inspections' 
      ? ['Property', 'ID', 'Postcode', 'Building Manager', 'Day', 'From', 'To', 'Type', 'Route', 'Description']
      : ['Property', 'ID', 'Postcode', 'Company', 'Date Live', 'AM', 'BM', 'Route', 'Status'];
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...data.map(item => {
        if (tableType === 'inspections') {
          return [
            `"${item.property.name}"`,
            item.id,
            `"${item.property.postcode}"`,
            `"${item.building_manager.name}"`,
            item.day,
            item.time_from,
            item.time_to,
            `"${item.type.name}"`,
            `"${item.route.name}"`,
            `"${item.description}"`
          ].join(',');
        } else {
          return [
            `"${item.property.name}"`,
            item.property.id,
            `"${item.property.postcode}"`,
            '"Sample Company"',
            '"2024-01-01"',
            '"John AM"',
            `"${item.building_manager.name}"`,
            `"${item.route.name}"`,
            '"To be Scheduled"'
          ].join(',');
        }
      })
    ].join('\n');
    
    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${tableType}_export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`${tableType.charAt(0).toUpperCase() + tableType.slice(1)} exported as CSV successfully!`);
  };

  const handleExportPDF = async (tableType = 'inspections') => {
    try {
      console.log(`📄 [FRONTEND] Starting PDF export for ${tableType}...`);
      
      const data = tableType === 'inspections' ? filteredInspections : filteredMonthlyInspections;
      
      // Create PDF content using jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text(`${tableType.charAt(0).toUpperCase() + tableType.slice(1)} Report`, 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${data.length}`, 20, 40);
      
      // Add table headers based on table type
      const headers = tableType === 'inspections' 
        ? ['Property', 'ID', 'Postcode', 'Building Manager', 'Day', 'From', 'To', 'Type', 'Route', 'Description']
        : ['Property', 'ID', 'Postcode', 'Company', 'Date Live', 'AM', 'BM', 'Route', 'Status'];
      
      let yPosition = 60;
      const startX = 20;
      const colWidth = 20;
      
      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + (index * colWidth), yPosition);
      });
      
      yPosition += 10;
      
      // Draw data rows
      data.forEach((item, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        
        const rowData = [];
        if (tableType === 'inspections') {
          rowData.push(
            String(item.property.name || '').substring(0, 15),
            String(item.id || '').substring(0, 15),
            String(item.property.postcode || '').substring(0, 15),
            String(item.building_manager.name || '').substring(0, 15),
            String(item.day || '').substring(0, 15),
            String(item.time_from || '').substring(0, 15),
            String(item.time_to || '').substring(0, 15),
            String(item.type.name || '').substring(0, 15),
            String(item.route.name || '').substring(0, 15),
            String(item.description || '').substring(0, 15)
          );
        } else {
          rowData.push(
            String(item.property.name || '').substring(0, 15),
            String(item.property.id || '').substring(0, 15),
            String(item.property.postcode || '').substring(0, 15),
            'Sample Company'.substring(0, 15),
            '2024-01-01'.substring(0, 15),
            'John AM'.substring(0, 15),
            String(item.building_manager.name || '').substring(0, 15),
            String(item.route.name || '').substring(0, 15),
            'To be Scheduled'.substring(0, 15)
          );
        }
        
        rowData.forEach((cell, cellIndex) => {
          const safeCell = String(cell || '');
          doc.text(safeCell.substring(0, 15), startX + (cellIndex * colWidth), yPosition);
        });
        
        yPosition += 7;
      });
      
      // Save PDF
      doc.save(`${tableType}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      console.log('PDF exported successfully!');
      toast.success(`${tableType.charAt(0).toUpperCase() + tableType.slice(1)} PDF exported successfully!`);
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error(`Failed to export PDF: ${error.message}`);
    }
  };

  const handleColumnVisibility = (tableType = 'inspections') => {
    setShowColumnVisibility(prev => ({
      ...prev,
      [tableType]: !prev[tableType]
    }));
  };

  // Handle table sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleMonthlySort = (field) => {
    if (monthlySortField === field) {
      setMonthlySortDirection(monthlySortDirection === "asc" ? "desc" : "asc");
    } else {
      setMonthlySortField(field);
      setMonthlySortDirection("asc");
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (window.confirm('Are you sure you want to remove this property and all related records?')) {
      try {
        // Call the API to delete the property and related records
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/properties/${propertyId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          
          // Remove from local state
          setMonthlyInspections(prev => prev.filter(inspection => inspection.property.id !== propertyId));
          setFilteredMonthlyInspections(prev => prev.filter(inspection => inspection.property.id !== propertyId));
          
          // Show success message with details about related records deleted
          const relatedCount = result.relatedRecordsDeleted || 0;
          if (relatedCount > 0) {
            toast.success(`Property and ${relatedCount} related records deleted successfully!`);
          } else {
            toast.success('Property deleted successfully!');
          }
        } else {
          throw new Error('Failed to delete property');
        }
      } catch (error) {
        console.error('Error deleting property:', error);
        toast.error('Failed to delete property. Please try again.');
      }
    }
  };

  const handleCopyAddress = (property) => {
    const fullAddress = `${property.name}, ${property.postcode}`;
    navigator.clipboard.writeText(fullAddress).then(() => {
      toast.success('Address copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullAddress;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Address copied to clipboard!');
    });
  };

  const toggleStatusDescriptions = () => {
    setIsStatusDescriptionsCollapsed(!isStatusDescriptionsCollapsed);
  };

  const toggleColumnVisibility = (tableType, columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [tableType]: {
        ...prev[tableType],
        [columnKey]: !prev[tableType][columnKey]
      }
    }));
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading inspections...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>

      {/* Status Overview */}
      <div className={styles.statusOverview}>
        <div className={styles.panelTitle}>
          <h3>Status overview</h3>
        </div>
        <div className={styles.panelGroup}>
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#5bc0de',
              opacity: selectedStatusFilter === null ? 1 : 0.7,
              cursor: 'pointer'
            }} 
            data-status-id="999"
            onClick={() => handleStatusBoxClick('999')}
          >
            <p className={styles.activityValue}>{inspectionsAll}</p>
            <p className={styles.activityTitle} data-to-display="all">All</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: 'green',
              opacity: selectedStatusFilter === 'upcoming' ? 1 : 0.7,
              cursor: 'pointer'
            }} 
            data-status-id="1"
            onClick={() => handleStatusBoxClick('1')}
          >
            <p className={styles.activityValue}>{inspectionsUpcoming}</p>
            <p className={styles.activityTitle} data-to-display="Upcoming">Upcoming</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: 'red',
              opacity: selectedStatusFilter === 'past' ? 1 : 0.7,
              cursor: 'pointer'
            }} 
            data-status-id="2"
            onClick={() => handleStatusBoxClick('2')}
          >
            <p className={styles.activityValue}>{inspectionsPast}</p>
            <p className={styles.activityTitle} data-to-display="Past">Past</p>
          </div>
        </div>
      </div>

      {/* Building Manager Actions */}
      <div className={styles.buildingManagerActions}>
        <div className={styles.actionGroup}>
          <span><strong>Go to building manager calendar: </strong></span>
          <select 
            className={styles.formControl}
            onChange={(e) => {
              if (e.target.value) {
                handleGoToBuildingManagerCalendar(e.target.value);
              }
            }}
          >
            <option value="">Please select</option>
            {buildingManagers.map(manager => (
              <option key={manager.id} value={manager.id}>{manager.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.actionGroup}>
          <span><strong>Send reminder email to: </strong></span>
          <select 
            className={styles.formControl}
            onChange={(e) => {
              if (e.target.value) {
                handleSendReminderEmail(e.target.value);
              }
            }}
          >
            <option value="">Please select</option>
            {buildingManagers.map(manager => (
              <option key={manager.id} value={manager.id}>{manager.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Panel */}
      <div className={styles.panel}>
        <div className={styles.panelTitle}>
          <h3>Inspections</h3>
          {canManageInspections && (
            <button className={styles.btnSecondary} onClick={handleAddInspection}>
              <i className="glyphicon glyphicon-plus"></i> Add Inspections
            </button>
          )}
        </div>
        
        <div className={styles.panelBody}>
          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Building Manager</label>
                <div className={styles.formControlWrapper}>
                  <select 
                    className={styles.formControl}
                    value={searchParams.building_manager_id}
                    onChange={(e) => handleSearchChange('building_manager_id', e.target.value)}
                  >
                    <option value="all">All</option>
                    {buildingManagers.map(manager => (
                      <option key={manager.id} value={manager.id}>{manager.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>From</label>
                <div className={styles.formControlWrapper}>
                  <input 
                    type="text"
                    className={`${styles.formControl} js-datepicker`}
                    placeholder="mm/dd/yyyy"
                    value={searchParams.date_from}
                    onChange={(e) => handleSearchChange('date_from', e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>To</label>
                <div className={styles.formControlWrapper}>
                  <input 
                    type="text"
                    className={`${styles.formControl} js-datepicker`}
                    placeholder="mm/dd/yyyy"
                    value={searchParams.date_to}
                    onChange={(e) => handleSearchChange('date_to', e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Type</label>
                <div className={styles.formControlWrapper}>
                  <select 
                    className={styles.formControl}
                    value={searchParams.type_id}
                    onChange={(e) => handleSearchChange('type_id', e.target.value)}
                  >
                    <option value="all">All</option>
                    {types.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.controlLabel}>Search</label>
              <div className={styles.formControlWrapper}>
                <input 
                  type="text"
                  className={styles.formControl}
                  placeholder="Search inspections..."
                  value={searchParams.searchTerm || ''}
                  onChange={(e) => handleSearchChange('searchTerm', e.target.value)}
                />
              </div>
            </div>

            <div className={styles.searchButtonContainer}>
              <button type="submit" className={styles.btnPrimary}>Search</button>
            </div>
          </form>

          {/* Hidden form field for status filtering */}
          <input type="hidden" name="inspectionsClickedStatusId" value="1" />

          {/* Inspections Table */}
          {inspections.length > 0 ? (
            <div className={styles.tableWrapper}>
              {/* DataTables Control Bar */}
              <div className={styles.dataTableControls}>
                <div className={styles.entriesControl}>
                  <span>Show</span>
                  <select 
                    className={styles.entriesSelect}
                    value={entriesPerPage}
                    onChange={(e) => handleInspectionsEntriesPerPageChange(parseInt(e.target.value))}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="30">30</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span>entries</span>
                  <span className={styles.resultCount}>
                    (Showing {filteredInspections.length > 0 ? ((currentPageInspections - 1) * entriesPerPage + 1) : 0} to {Math.min(currentPageInspections * entriesPerPage, getTotalFilteredInspections())} of {getTotalFilteredInspections()} results)
                  </span>
                </div>
                
                <div className={styles.searchControl}>
                  <span>Search:</span>
                  <input 
                    type="text" 
                    className={styles.searchInput}
                    placeholder="Search inspections..."
                    value={tableSearch}
                    onChange={(e) => setTableSearch(e.target.value)}
                  />
                </div>
                
                <div className={styles.actionButtons}>
                  <button className={styles.exportBtn} onClick={() => handleExportCSV('inspections')}>Export as CSV</button>
                  <button className={styles.exportBtn} onClick={() => handleExportPDF('inspections')}>Export as PDF</button>
                  <button className={styles.visibilityBtn} onClick={() => handleColumnVisibility('inspections')}>Column visibility</button>
                </div>
                
                {/* Column Visibility Dropdown */}
                {showColumnVisibility.inspections && (
                  <div className={styles.columnVisibilityDropdown}>
                    <h4>Toggle Columns:</h4>
                    {Object.entries(columnVisibility.inspections).map(([key, visible]) => (
                      <label key={key} className={styles.columnToggle}>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleColumnVisibility('inspections', key)}
                        />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    {columnVisibility.inspections.property && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("property")}
                        style={{ cursor: "pointer" }}
                      >
                        Property<SortIcon direction={sortField === "property" ? sortDirection : null} isActive={sortField === "property"} />
                      </th>
                    )}
                    {columnVisibility.inspections.id && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("id")}
                        style={{ cursor: "pointer" }}
                      >
                        ID<SortIcon direction={sortField === "id" ? sortDirection : null} isActive={sortField === "id"} />
                      </th>
                    )}
                    {columnVisibility.inspections.postcode && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("postcode")}
                        style={{ cursor: "pointer" }}
                      >
                        Postcode<SortIcon direction={sortField === "postcode" ? sortDirection : null} isActive={sortField === "postcode"} />
                      </th>
                    )}
                    {columnVisibility.inspections.buildingManager && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("buildingManager")}
                        style={{ cursor: "pointer" }}
                      >
                        Building Manager<SortIcon direction={sortField === "buildingManager" ? sortDirection : null} isActive={sortField === "buildingManager"} />
                      </th>
                    )}
                    {columnVisibility.inspections.day && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("day")}
                        style={{ cursor: "pointer" }}
                      >
                        Day<SortIcon direction={sortField === "day" ? sortDirection : null} isActive={sortField === "day"} />
                      </th>
                    )}
                    {columnVisibility.inspections.from && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("from")}
                        style={{ cursor: "pointer" }}
                        title="Time From"
                      >
                        From<SortIcon direction={sortField === "from" ? sortDirection : null} isActive={sortField === "from"} />
                      </th>
                    )}
                    {columnVisibility.inspections.to && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("to")}
                        style={{ cursor: "pointer" }}
                        title="Time To"
                      >
                        To<SortIcon direction={sortField === "to" ? sortDirection : null} isActive={sortField === "to"} />
                      </th>
                    )}
                    {columnVisibility.inspections.type && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("type")}
                        style={{ cursor: "pointer" }}
                      >
                        Type<SortIcon direction={sortField === "type" ? sortDirection : null} isActive={sortField === "type"} />
                      </th>
                    )}
                    {columnVisibility.inspections.route && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("route")}
                        style={{ cursor: "pointer" }}
                      >
                        Route<SortIcon direction={sortField === "route" ? sortDirection : null} isActive={sortField === "route"} />
                      </th>
                    )}
                    {columnVisibility.inspections.description && (
                      <th 
                        className="sortable"
                        onClick={() => handleSort("description")}
                        style={{ cursor: "pointer" }}
                      >
                        Description<SortIcon direction={sortField === "description" ? sortDirection : null} isActive={sortField === "description"} />
                      </th>
                    )}
                    {columnVisibility.inspections.calendar && <th>Calendar</th>}
                    {columnVisibility.inspections.actions && <th className={styles.actionHead}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections.map((inspection) => (
                    <tr key={inspection.id}>
                      {columnVisibility.inspections.property && <td>{inspection.property.name}</td>}
                      {columnVisibility.inspections.id && <td>{inspection.id}</td>}
                      {columnVisibility.inspections.postcode && <td>{inspection.property.postcode}</td>}
                      {columnVisibility.inspections.buildingManager && <td>{inspection.building_manager.name}</td>}
                      {columnVisibility.inspections.day && <td>{inspection.day}</td>}
                      {columnVisibility.inspections.from && <td>{inspection.time_from}</td>}
                      {columnVisibility.inspections.to && <td>{inspection.time_to}</td>}
                      {columnVisibility.inspections.type && <td>{inspection.type.name}</td>}
                      {columnVisibility.inspections.route && <td>{inspection.route.name}</td>}
                      {columnVisibility.inspections.description && <td>{inspection.description}</td>}
                      {columnVisibility.inspections.calendar && (
                        <td>
                          <button 
                            className={styles.btnCalendar}
                            onClick={() => navigate(`/inspections/property/${inspection.property.id}`)}
                          >
                            Calendar
                          </button>
                        </td>
                      )}
                      {columnVisibility.inspections.actions && (
                        <td className={styles.actionColumn}>
                          {canManageInspections && (
                            <>
                              <button 
                                className={styles.btnEdit}
                                onClick={() => handleEditInspection(inspection.id)}
                                title="Edit inspection"
                              >
                                Edit
                              </button>
                              <button 
                                className={styles.btnDelete}
                                onClick={() => handleDeleteInspection(inspection.id)}
                                title="Remove inspection"
                              >
                                Remove
                              </button>
                            </>
                          )}
                          <button 
                            className={styles.btnMonthlyInspections}
                            onClick={() => navigate(`/properties/${inspection.property.id}/monthly-inspections`)}
                            title="Manage monthly inspections"
                          >
                            Monthly Inspections
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination Controls for Inspections Table */}
              {(() => {
                const totalFiltered = getTotalFilteredInspections();
                const totalPages = Math.ceil(totalFiltered / entriesPerPage) || 1;
                const startPage = Math.max(1, currentPageInspections - 2);
                const endPage = Math.min(totalPages, currentPageInspections + 2);
                const pageNumbers = [];
                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(i);
                }
                
                return totalPages > 1 ? (
                  <div className={styles.pagination}>
                    <button
                      className={styles.paginationBtn}
                      onClick={() => handleInspectionsPageChange(1)}
                      disabled={currentPageInspections === 1}
                    >
                      First
                    </button>
                    <button
                      className={styles.paginationBtn}
                      onClick={() => handleInspectionsPageChange(currentPageInspections - 1)}
                      disabled={currentPageInspections === 1}
                    >
                      Previous
                    </button>
                    {pageNumbers.map(pageNum => (
                      <button
                        key={pageNum}
                        className={`${styles.paginationBtn} ${currentPageInspections === pageNum ? styles.paginationBtnActive : ''}`}
                        onClick={() => handleInspectionsPageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      className={styles.paginationBtn}
                      onClick={() => handleInspectionsPageChange(currentPageInspections + 1)}
                      disabled={currentPageInspections === totalPages}
                    >
                      Next
                    </button>
                    <button
                      className={styles.paginationBtn}
                      onClick={() => handleInspectionsPageChange(totalPages)}
                      disabled={currentPageInspections === totalPages}
                    >
                      Last
                    </button>
                    <span className={styles.paginationInfo}>
                      Page {currentPageInspections} of {totalPages}
                    </span>
                  </div>
                ) : null;
              })()}
            </div>
          ) : (
            <div className={styles.noResults}>No inspections!</div>
          )}
        </div>
      </div>

      {/* Properties Overview */}
      <div className={styles.propertiesOverview}>
        <div className={styles.panelTitle}>
          <h3>Properties overview</h3>
        </div>
        <div className={styles.panelGroup}>
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#5bc0de',
              border: selectedPropertyStatusFilter === 'upcoming' ? '3px solid #000' : '1px solid transparent',
              opacity: selectedPropertyStatusFilter && selectedPropertyStatusFilter !== 'upcoming' ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onClick={() => handleActivityBoxClick('upcoming')}
          >
            <p className={styles.activityValue}>{propertiesUpcoming}</p>
            <p className={styles.activityTitle}>To be Scheduled</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#f0ad4e',
              border: selectedPropertyStatusFilter === 'scheduled' ? '3px solid #000' : '1px solid transparent',
              opacity: selectedPropertyStatusFilter && selectedPropertyStatusFilter !== 'scheduled' ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onClick={() => handleActivityBoxClick('scheduled')}
          >
            <p className={styles.activityValue}>{propertiesInspectionScheduled}</p>
            <p className={styles.activityTitle}>Inspection Scheduled</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#5cb85c',
              border: selectedPropertyStatusFilter === 'completed' ? '3px solid #000' : '1px solid transparent',
              opacity: selectedPropertyStatusFilter && selectedPropertyStatusFilter !== 'completed' ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onClick={() => handleActivityBoxClick('completed')}
          >
            <p className={styles.activityValue}>{propertiesInspectionCompleted}</p>
            <p className={styles.activityTitle}>Completed</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#337ab7',
              border: selectedPropertyStatusFilter === 'to_be_published' ? '3px solid #000' : '1px solid transparent',
              opacity: selectedPropertyStatusFilter && selectedPropertyStatusFilter !== 'to_be_published' ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onClick={() => handleActivityBoxClick('to_be_published')}
          >
            <p className={styles.activityValue}>{propertiesToBePublished}</p>
            <p className={styles.activityTitle}>To be published</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#5cb85c',
              border: selectedPropertyStatusFilter === 'past_completed' ? '3px solid #000' : '1px solid transparent',
              opacity: selectedPropertyStatusFilter && selectedPropertyStatusFilter !== 'past_completed' ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onClick={() => handleActivityBoxClick('past_completed')}
          >
            <p className={styles.activityValue}>{propertiesCompleted}</p>
            <p className={styles.activityTitle}>Past Inspection To Complete</p>
          </div>
          
          <div 
            className={styles.activityBox} 
            style={{ 
              backgroundColor: '#d9534f',
              border: selectedPropertyStatusFilter === 'overdue' ? '3px solid #000' : '1px solid transparent',
              opacity: selectedPropertyStatusFilter && selectedPropertyStatusFilter !== 'overdue' ? 0.5 : 1,
              cursor: 'pointer'
            }}
            onClick={() => handleActivityBoxClick('overdue')}
          >
            <p className={styles.activityValue}>{propertiesOverdue}</p>
            <p className={styles.activityTitle}>Overdue</p>
          </div>
        </div>
      </div>

      {/* Status Descriptions Panel */}
      <div className={styles.statusDescriptionsPanel}>
        <div 
          className={styles.panelTitle} 
          onClick={toggleStatusDescriptions}
          style={{ cursor: 'pointer' }}
        >
          <h3>Descriptions of statuses</h3>
          <i 
            className={`fa fa-arrow-${isStatusDescriptionsCollapsed ? 'down' : 'up'}`} 
            aria-hidden="true"
            style={{ display: 'inline-block' }}
          ></i>
        </div>
        <div 
          className={styles.panelBody}
          style={{ 
            display: isStatusDescriptionsCollapsed ? 'none' : 'block',
            transition: 'all 0.3s ease'
          }}
        >
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#5bc0de' }}>
              <strong>To be Scheduled</strong>
            </span> - there is no inspection for this month
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#f0ad4e' }}>
              <strong>Inspection Scheduled</strong>
            </span> - monthly inspection was created and is upcoming
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#5cb85c' }}>
              <strong>Completed</strong>
            </span> - monthly inspection was done
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#337ab7' }}>
              <strong>To be published</strong>
            </span> - report is not published
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#5cb85c' }}>
              <strong>Past Inspection To Complete</strong>
            </span> - all Monthly Inspection Reports are added
          </div>
          <div className={styles.statusDescription}>
            <span className={styles.statusLabel} style={{ backgroundColor: '#d9534f' }}>
              <strong>Overdue</strong>
            </span> - lack of at least one published Monthly Inspection Report
          </div>
        </div>
      </div>

      {/* Monthly Inspections Panel */}
      <div className={styles.monthlyInspectionsPanel}>
        <div className={styles.panelTitle}>
          <h3>Monthly Inspections</h3>
        </div>
        <div className={styles.panelBody}>
          {/* Monthly Inspections Search Form */}
          <form className={styles.monthlySearchForm} onSubmit={handleMonthlySearchSubmit}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Account Manager</label>
                <div className={styles.formControlWrapper}>
                  <select 
                    className={styles.formControl}
                    value={monthlySearchParams.account_manager_id}
                    onChange={(e) => handleMonthlySearchChange('account_manager_id', e.target.value)}
                  >
                    <option value="all">All</option>
                    {accountManagers.map(manager => (
                      <option key={manager.id} value={manager.id}>{manager.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Route</label>
                <div className={styles.formControlWrapper}>
                  <select 
                    className={styles.formControl}
                    value={monthlySearchParams.route_id}
                    onChange={(e) => handleMonthlySearchChange('route_id', e.target.value)}
                  >
                    <option value="all">All</option>
                    {routes.map(route => (
                      <option key={route.id} value={route.id}>{route.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Client</label>
                <div className={styles.formControlWrapper} style={{ position: 'relative' }}>
                  <input 
                    type="text"
                    className={`${styles.formControl} typeahead typeahead-client js-client-field-typeahead`}
                    placeholder="Type client name"
                    style={{ border: '1px solid #0a7280', width: '100%' }}
                    value={monthlySearchParams.client_name}
                    onChange={(e) => handleClientSearch(e.target.value)}
                    onBlur={() => {
                      // Delay closing to allow click on dropdown item to register
                      setTimeout(() => setShowClientTypeahead(false), 200);
                    }}
                    onFocus={() => {
                      if (clientTypeaheadResults.length > 0) {
                        setShowClientTypeahead(true);
                      }
                    }}
                    autoComplete="off"
                  />
                  <input type="hidden" name="client_id" value={monthlySearchParams.client_id} />
                  {showClientTypeahead && clientTypeaheadResults.length > 0 && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderTop: 'none',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseDown={(e) => e.preventDefault()} // Prevent input from losing focus
                    >
                      {clientTypeaheadResults.map((client) => (
                        <div
                          key={client.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleClientSelect(client);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <strong>{client.name}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Property</label>
                <div className={styles.formControlWrapper} style={{ position: 'relative' }}>
                  <input 
                    type="text"
                    className={`${styles.formControl} typeahead typeahead-property js-property-field-typeahead`}
                    placeholder="Type property name"
                    style={{ border: '1px solid #0a7280', width: '100%' }}
                    value={monthlySearchParams.property_name}
                    onChange={(e) => handlePropertySearch(e.target.value)}
                    onBlur={() => {
                      // Delay closing to allow click on dropdown item to register
                      setTimeout(() => setShowPropertyTypeahead(false), 200);
                    }}
                    onFocus={() => {
                      if (propertyTypeaheadResults.length > 0) {
                        setShowPropertyTypeahead(true);
                      }
                    }}
                    autoComplete="off"
                  />
                  <input type="hidden" name="property_id" value={monthlySearchParams.property_id} />
                  {showPropertyTypeahead && propertyTypeaheadResults.length > 0 && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderTop: 'none',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onMouseDown={(e) => e.preventDefault()} // Prevent input from losing focus
                    >
                      {propertyTypeaheadResults.map((property) => (
                        <div
                          key={property.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handlePropertySelect(property);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        >
                          <strong>{property.name} {property.property_id ? `(${property.property_id})` : ''}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.searchButtonContainer}>
                <button type="submit" className={styles.btnPrimary}>Search</button>
              </div>
            </div>
          </form>

          {/* Monthly Inspections Table */}
          <div className={styles.tableWrapper}>
            {/* DataTables Control Bar */}
            <div className={styles.dataTableControls}>
              <div className={styles.entriesControl}>
                <span>Show</span>
                <select 
                  className={styles.entriesSelect}
                  value={monthlyEntriesPerPage}
                  onChange={(e) => handleMonthlyEntriesPerPageChange(parseInt(e.target.value))}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="30">30</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>entries</span>
                <span className={styles.resultCount}>
                  (Showing {filteredMonthlyInspections.length > 0 ? ((currentPageMonthly - 1) * monthlyEntriesPerPage + 1) : 0} to {Math.min(currentPageMonthly * monthlyEntriesPerPage, getTotalFilteredMonthlyInspections())} of {getTotalFilteredMonthlyInspections()} results)
                </span>
              </div>
              
              <div className={styles.searchControl}>
                <span>Search:</span>
                <input 
                  type="text" 
                  className={styles.searchInput}
                  placeholder="Search properties..."
                  value={monthlyTableSearch}
                  onChange={(e) => setMonthlyTableSearch(e.target.value)}
                />
              </div>
              
                              <div className={styles.actionButtons}>
                  <button className={styles.exportBtn} onClick={() => handleExportCSV('monthly')}>Export as CSV</button>
                  <button className={styles.exportBtn} onClick={() => handleExportPDF('monthly')}>Export as PDF</button>
                  <button className={styles.visibilityBtn} onClick={() => handleColumnVisibility('monthly')}>Column visibility</button>
                </div>
                
                {/* Column Visibility Dropdown */}
                {showColumnVisibility.monthly && (
                  <div className={styles.columnVisibilityDropdown}>
                    <h4>Toggle Columns:</h4>
                    {Object.entries(columnVisibility.monthly).map(([key, visible]) => (
                      <label key={key} className={styles.columnToggle}>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleColumnVisibility('monthly', key)}
                        />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    ))}
                  </div>
                )}
            </div>
            
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  {columnVisibility.monthly.property && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("property")}
                      style={{ cursor: "pointer" }}
                    >
                      Property<SortIcon direction={monthlySortField === "property" ? monthlySortDirection : null} isActive={monthlySortField === "property"} />
                    </th>
                  )}
                  {columnVisibility.monthly.id && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("id")}
                      style={{ cursor: "pointer" }}
                    >
                      ID<SortIcon direction={monthlySortField === "id" ? monthlySortDirection : null} isActive={monthlySortField === "id"} />
                    </th>
                  )}
                  {columnVisibility.monthly.postcode && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("postcode")}
                      style={{ cursor: "pointer" }}
                    >
                      Postcode<SortIcon direction={monthlySortField === "postcode" ? monthlySortDirection : null} isActive={monthlySortField === "postcode"} />
                    </th>
                  )}
                  {columnVisibility.monthly.company && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("company")}
                      style={{ cursor: "pointer" }}
                    >
                      Company<SortIcon direction={monthlySortField === "company" ? monthlySortDirection : null} isActive={monthlySortField === "company"} />
                    </th>
                  )}
                  {columnVisibility.monthly.dateLive && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("dateLive")}
                      style={{ cursor: "pointer" }}
                    >
                      Date Live<SortIcon direction={monthlySortField === "dateLive" ? monthlySortDirection : null} isActive={monthlySortField === "dateLive"} />
                    </th>
                  )}
                  {columnVisibility.monthly.am && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("am")}
                      style={{ cursor: "pointer" }}
                    >
                      AM<SortIcon direction={monthlySortField === "am" ? monthlySortDirection : null} isActive={monthlySortField === "am"} />
                    </th>
                  )}
                  {columnVisibility.monthly.bm && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("bm")}
                      style={{ cursor: "pointer" }}
                    >
                      BM<SortIcon direction={monthlySortField === "bm" ? monthlySortDirection : null} isActive={monthlySortField === "bm"} />
                    </th>
                  )}
                  {columnVisibility.monthly.route && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("route")}
                      style={{ cursor: "pointer" }}
                    >
                      Route<SortIcon direction={monthlySortField === "route" ? monthlySortDirection : null} isActive={monthlySortField === "route"} />
                    </th>
                  )}
                  {columnVisibility.monthly.status && (
                    <th 
                      className="sortable"
                      onClick={() => handleMonthlySort("status")}
                      style={{ cursor: "pointer" }}
                    >
                      Status<SortIcon direction={monthlySortField === "status" ? monthlySortDirection : null} isActive={monthlySortField === "status"} />
                    </th>
                  )}
                  {columnVisibility.monthly.actions && <th className={styles.actionHead}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {monthlyInspectionsLoading ? (
                  <tr>
                    <td colSpan={Object.values(columnVisibility.monthly).filter(v => v).length} style={{ textAlign: 'center', padding: '40px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
                          <span className="sr-only">Loading...</span>
                        </div>
                        <p>Loading monthly inspections...</p>
                      </div>
                    </td>
                  </tr>
                ) : filteredMonthlyInspections.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(columnVisibility.monthly).filter(v => v).length} style={{ textAlign: 'center', padding: '40px' }}>
                      {selectedPropertyStatusFilter ? (
                        <div>
                          <p style={{ fontSize: '16px', marginBottom: '10px' }}>No properties found with the selected status.</p>
                          <p style={{ color: '#666' }}>Try selecting a different status filter from the Properties overview above.</p>
                        </div>
                      ) : (
                        <div>
                          <p style={{ fontSize: '16px', marginBottom: '10px' }}>No data loaded.</p>
                          <p style={{ color: '#666' }}>Please click on a status filter from the Properties overview above to load properties.</p>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredMonthlyInspections.map((property) => (
                  <tr key={`monthly-${property.id}`}>
                    {columnVisibility.monthly.property && <td><Link to={`/properties/${property.property.id}`} className={styles.propertyLink}>{property.property.name}</Link></td>}
                    {columnVisibility.monthly.id && <td>{property.property.property_id || property.property.id}</td>}
                    {columnVisibility.monthly.postcode && <td>{property.property.postcode}</td>}
                    {columnVisibility.monthly.company && <td>{property.client.name}</td>}
                    {columnVisibility.monthly.dateLive && <td>{property.date_live || '-'}</td>}
                    {columnVisibility.monthly.am && <td>{property.accountManager}</td>}
                    {columnVisibility.monthly.bm && <td>{property.buildingManager.name}</td>}
                    {columnVisibility.monthly.route && <td>{property.route.name}</td>}
                    {columnVisibility.monthly.status && (
                      <td>
                        <span className={styles.statusBadge} style={{ backgroundColor: property.statusColor || '#5bc0de' }}>
                          {property.monthly_inspection_status}
                        </span>
                      </td>
                    )}
                    {columnVisibility.monthly.actions && (
                      <td className={styles.actionColumn}>
                        <button
                          className={styles.btnShow}
                          onClick={() => navigate(`/properties/${property.property.id}/monthly-inspections`)}
                          title="Show monthly inspection form"
                        >
                          Show
                        </button>
                        {!isGuardianManager() && (
                          <button
                            className={styles.btnEdit}
                            onClick={() => navigate(`/properties/${property.property.id}/edit`)}
                            title="Edit property"
                          >
                            Edit
                          </button>
                        )}
                        {!isGuardianManager() && (
                          <button
                            className={styles.btnPayments}
                            onClick={() => navigate(`/properties/${property.property.id}/payments`)}
                            title="View payments"
                          >
                            Payments
                          </button>
                        )}
                        {!isGuardianManager() && (
                          <button
                            className={styles.btnPhotos}
                            onClick={() => navigate(`/properties/${property.property.id}/photos`)}
                            title="Manage photos"
                          >
                            Photos
                          </button>
                        )}
                        <button
                          className={styles.btnViewings}
                          onClick={() => navigate(`/properties/${property.property.id}/viewings`)}
                          title="View property viewings"
                        >
                          Viewings
                        </button>
                        <button
                          className={styles.btnMonthlyInspections}
                          onClick={() => navigate(`/properties/${property.property.id}/monthly-inspections`)}
                          title="Manage monthly inspections"
                        >
                          Monthly Inspections
                        </button>
                        <button
                          className={styles.btnCopyAddress}
                          onClick={() => handleCopyAddress(property.property)}
                          title="Copy property address"
                        >
                          <i className="glyphicon glyphicon-road"></i> Copy Address
                        </button>
                        {!isGuardianManager() && (
                          <button
                            className={styles.btnDelete}
                            onClick={() => handleDeleteProperty(property.property.id)}
                            title="Delete property"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {/* Pagination Controls for Monthly Inspections Table */}
            {(() => {
              const totalFiltered = getTotalFilteredMonthlyInspections();
              const totalPages = Math.ceil(totalFiltered / monthlyEntriesPerPage) || 1;
              const startPage = Math.max(1, currentPageMonthly - 2);
              const endPage = Math.min(totalPages, currentPageMonthly + 2);
              const pageNumbers = [];
              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
              }
              
              return totalPages > 1 ? (
                <div className={styles.pagination}>
                  <button
                    className={styles.paginationBtn}
                    onClick={() => handleMonthlyPageChange(1)}
                    disabled={currentPageMonthly === 1}
                  >
                    First
                  </button>
                  <button
                    className={styles.paginationBtn}
                    onClick={() => handleMonthlyPageChange(currentPageMonthly - 1)}
                    disabled={currentPageMonthly === 1}
                  >
                    Previous
                  </button>
                  {pageNumbers.map(pageNum => (
                    <button
                      key={pageNum}
                      className={`${styles.paginationBtn} ${currentPageMonthly === pageNum ? styles.paginationBtnActive : ''}`}
                      onClick={() => handleMonthlyPageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    className={styles.paginationBtn}
                    onClick={() => handleMonthlyPageChange(currentPageMonthly + 1)}
                    disabled={currentPageMonthly === totalPages}
                  >
                    Next
                  </button>
                  <button
                    className={styles.paginationBtn}
                    onClick={() => handleMonthlyPageChange(totalPages)}
                    disabled={currentPageMonthly === totalPages}
                  >
                    Last
                  </button>
                  <span className={styles.paginationInfo}>
                    Page {currentPageMonthly} of {totalPages}
                  </span>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      </div>

    </div>
  );
};

export default Inspections; 